import { COUNTRY_CODES } from "../constants/countries";

/**
 * El identificador fiscal de la empresa, tal como se imprime en el tiquete.
 *
 * El tiquete genérico leía `VAT_Registration_No` a secas y, si venía vacío,
 * imprimía el texto «Sin RUC». Eso funciona donde el identificador de la empresa
 * se llama RUC y viaja en ese campo --Ecuador, Perú-- y falla de dos maneras
 * distintas donde no:
 *
 *  - **Imprime un número que no está.** En Puerto Rico `VAT_Registration_No`
 *    está vacío (medido el 16/10/2026 en Santillana Puerto Rico) y el
 *    identificador real es el EIN del IRS, que Business Central guarda en
 *    `Federal_ID_No`: ahí sí está, `66-0472230`.
 *  - **Lo llama por un nombre que no existe.** En Puerto Rico no hay ningún
 *    «RUC», así que un tiquete que dice «Sin RUC» no informa de nada: nombra
 *    algo que ese país no tiene.
 *
 * Honduras y Costa Rica ya resolvieron esto cada uno en su propio tiquete
 * --«R.T.N:» y «Cédula Jurídica:»--, así que lo único que faltaba era que el
 * tiquete genérico dejara de dar por supuesto el caso de Ecuador.
 */
const POR_PAIS = {
  [COUNTRY_CODES.PUERTORICO]: {
    campo: "Federal_ID_No",
    ausente: "Sin EIN",
  },
};

/**
 * Lo de siempre, para los países que no declaran nada.
 *
 * Se deja idéntico a lo que había --mismo campo, mismo texto-- a propósito: el
 * objetivo era dejar de imprimir mal el de Puerto Rico, no cambiarle el papel a
 * los demás países sin que nadie lo haya pedido.
 */
const POR_DEFECTO = { campo: "VAT_Registration_No", ausente: "Sin RUC" };

/**
 * @param {object} empresa Fila de la entidad `Empresa` de Business Central.
 * @param {string} pais    Código del país activo (`country?.code`).
 * @returns {string} El identificador, o el texto de ausencia del país.
 */
export const identificadorFiscalDeLaEmpresa = (empresa, pais) => {
  const { campo, ausente } = POR_PAIS[pais] || POR_DEFECTO;

  // Se intenta el campo del país y después el de siempre. El segundo intento no
  // es por si acaso: es para que el día que alguien rellene el campo estándar en
  // Business Central el tiquete lo imprima, en vez de seguir diciendo que falta.
  const delPais = String(empresa?.[campo] ?? "").trim();
  if (delPais) return delPais;

  const estandar = String(empresa?.[POR_DEFECTO.campo] ?? "").trim();
  return estandar || ausente;
};
